import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Zap, Lock, Eye, Search } from 'lucide-react';
import { Button, Badge, Card, CardContent } from '@devkit/ui';
import {
  getCategories,
  getCategoryCounts,
  getPopularTools,
  type ToolCategory,
} from '../lib/toolRegistry';
import { SEOHead } from '../components/seo/SEOHead';
import { buildOrganizationLd, buildWebSiteLd, buildItemListLd } from '../lib/seoJsonLd';
import { useUI } from '../store/ui';

const CATEGORY_ICON: Record<string, string> = {
  json: `text-yellow-400 bg-yellow-400/10`,
  encoding: `text-blue-400 bg-blue-400/10`,
  security: `text-purple-400 bg-purple-400/10`,
  regex: `text-green-400 bg-green-400/10`,
  time: `text-orange-400 bg-orange-400/10`,
  text: `text-cyan-400 bg-cyan-400/10`,
  utility: `text-pink-400 bg-pink-400/10`,
};

const FEATURES = [
  {
    icon: Zap,
    title: `Lightning Fast`,
    description: `All tools run instantly in your browser. No server round-trips, no loading spinners.`,
  },
  {
    icon: Shield,
    title: `Privacy First`,
    description: `Your data never leaves your device. We don't collect, store, or transmit any input.`,
  },
  {
    icon: Lock,
    title: `No Signup`,
    description: `Start using any tool immediately. No account required, no email verification.`,
  },
  {
    icon: Eye,
    title: `Transparent`,
    description: `Open source components. Know exactly what your data does (or doesn't do).`,
  },
];

interface HomePageProps {
  onOpenSearch?: () => void;
}

export default function HomePage(_: HomePageProps = {}) {
  const openPalette = useUI((s) => s.setPaletteOpen);
  const popular = getPopularTools();
  const categories = getCategories();
  const counts = getCategoryCounts();
  const jsonLd = [
    buildOrganizationLd(),
    buildWebSiteLd(),
    buildItemListLd(
      `Popular DevKit tools`,
      popular.map((t) => ({ name: t.name, url: `https://dditzy.com/${t.slug}`, description: t.shortDescription }))
    ),
  ];

  return (
    <div className="animate-fade-in">
      <SEOHead
        title={`DevKit — Fast, Private, Developer Tools`}
        description={`Free online developer tools that respect your privacy. Format JSON, decode JWTs, encode Base64, and more — all in your browser.`}
        keywords={[`developer tools`, `json formatter`, `jwt decoder`, `base64 encoder`, `regex tester`, `online tools`, `free tools`]}
        canonical={`https://dditzy.com/`}
        og={{ title: `DevKit — Fast, Private, Developer Tools`, description: `Format JSON, decode JWTs, encode Base64, and more — all in your browser.`, url: `https://dditzy.com/`, type: `website` }}
        twitter={{ card: `summary_large_image`, title: `DevKit — Fast, Private, Developer Tools`, description: `Free online developer tools that respect your privacy.` }}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-3xl" />

        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 text-balance">
              Developer tools,
              <br />
              <span className="gradient-text">without the hassle.</span>
            </h1>

            <p className="text-lg md:text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
              Fast, private, and free tools for developers. Most tools run entirely in your
              browser — your data never leaves your device.
            </p>

            {/* Hero search — opens the command palette. */}
            <div className="max-w-xl mx-auto mb-8">
              <button
                type="button"
                onClick={() => openPalette(true)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700 hover:border-slate-600 text-slate-400 hover:text-white transition-colors text-left"
                aria-label="Search developer tools"
              >
                <Search className="w-5 h-5" aria-hidden />
                <span className="flex-1 text-base">Search developer tools…</span>
                <kbd className="hidden sm:inline-block text-xs font-mono px-1.5 py-0.5 rounded bg-slate-900 text-slate-500 border border-slate-700">
                  ⌘K
                </kbd>
              </button>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/tools">
                <Button size="lg" className="group">
                  Explore Tools
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/json-formatter">
                <Button variant="outline" size="lg">
                  Try JSON Formatter
                </Button>
              </Link>
            </div>

            <p className="mt-6 text-sm text-slate-500">
              No signup • No uploads • No tracking of your data
            </p>
          </div>
        </div>
      </section>

      {/* Popular Tools */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-white">Popular Tools</h2>
              <p className="text-slate-400 mt-1">Most used by developers worldwide</p>
            </div>
            <Link to="/tools">
              <Button variant="ghost" className="group">
                View All
                <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {popular.map((tool) => (
              <Link key={tool.slug} to={`/${tool.slug}`}>
                <Card className="card-hover cursor-pointer h-full">
                  <CardContent className="p-4 flex flex-col items-center text-center">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                        CATEGORY_ICON[tool.category] ?? `text-slate-400 bg-slate-400/10`
                      }`}
                    >
                      <Zap className="w-6 h-6" />
                    </div>
                    <h3 className="font-medium text-white text-sm">{tool.name}</h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 bg-surface/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-white">Browse by Category</h2>
            <p className="text-slate-400 mt-2">Find the right tool for your needs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {categories
              .filter((c) => counts[c.id])
              .map((category) => (
                <Link key={category.id} to={`/tools/${category.id}`}>
                  <Card className="card-hover cursor-pointer h-full">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          CATEGORY_ICON[category.id] ?? `text-primary bg-primary/10`
                        }`}
                      >
                        <Zap className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-white">{category.name}</h3>
                          <Badge variant="default" className="text-xs">
                            {counts[category.id as ToolCategory]}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-400 mt-1">{category.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-white">Why DevKit?</h2>
            <p className="text-slate-400 mt-2">Built with developers in mind</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {FEATURES.map((feature) => (
              <div key={feature.title} className="text-center">
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-t from-primary/10 to-transparent">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to speed up your workflow?
          </h2>
          <p className="text-slate-400 mb-8 max-w-lg mx-auto">
            Start using DevKit today. No signup required, completely free.
          </p>
          <Link to="/tools">
            <Button size="lg" className="group">
              Get Started
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}